import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  //Badge,
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  DatePicker,
  Flex,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectLabel,
  SelectItem,
  Separator,
  Text,
  Toggle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';
import type {
  IScheduleRepeatsChange,
  TDayOfWeek,
  TRepeatFrequency,
  TSchedulePreset,
} from './types';
import {
  DATE_FORMAT_TOKENS,
  getCurrentDate,
  isValidTimeStrict,
} from '@/shared/utils/datetime-utils';
import {
  DAYS_OF_WEEK,
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
  EMPTY_DAYS,
  FREQUENCY_ITEMS,
  PRESET_AUTOFILL,
  getDayOfWeekFromDate,
  getDefaultTimezone,
  getTimezoneGroupsWithFallback,
  getTimezoneLabel,
  sortDaysByWeekOrder,
} from './utils';
import { ScheduleInputDropdown } from './ScheduleInputDropdown';
import { SchedulePresets } from './SchedulePresets';

/**
 * Props for the ScheduleRepeatsCard component.
 */
export interface IScheduleRepeatsCardProps {
  date?: Date;
  interval?: number;
  frequency?: TRepeatFrequency;
  selectedDays?: TDayOfWeek[];
  selectedPreset?: TSchedulePreset;
  repeatStartTime?: string;
  repeatEndTime?: string;
  timezone?: string;
  onChange?: (changes: IScheduleRepeatsChange) => void;
  readonly?: boolean;
}

/**
 * A card component for configuring schedule repeat patterns.
 * Allows users to set frequency, days, time range, and select from preset options.
 *
 * @param props - Component props
 * @returns JSX element containing the schedule repeats card
 */
export function ScheduleRepeatsCard({
  date,
  interval,
  frequency,
  selectedDays = EMPTY_DAYS,
  selectedPreset,
  repeatStartTime,
  // TODO: Re-enable when backend supports active_window
  // repeatEndTime,
  timezone,
  onChange,
  readonly = false,
}: IScheduleRepeatsCardProps) {
  const { t } = useTranslation('automation');

  // Internal state initialized from props, with fallbacks to defaults
  const [startTime, setStartTime] = useState<string>(
    repeatStartTime ?? DEFAULT_START_TIME,
  );
  // TODO: Re-enable when backend supports active_window
  // const [endTime, setEndTime] = useState<string>(
  //   repeatEndTime ?? DEFAULT_END_TIME,
  // );
  const [internalTimezone, setInternalTimezone] = useState<string>(
    timezone ?? getDefaultTimezone(),
  );
  const [selectedFrequency, setSelectedFrequency] = useState<string>(
    frequency ?? 'Week',
  );

  const [today] = useState(() => getCurrentDate());
  // buildScheduleConfig anchors monthly/yearly runs on the start date, so presets must too
  const referenceDate = date ?? today;

  // Tracks interval
  const handleIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    // Clear preset when user manually edits (only if preset is selected)
    if (selectedPreset) {
      onChange?.({ selectedPreset: undefined });
    }

    // Allow clearing the input - pass undefined to show placeholder
    if (rawValue === '') {
      onChange?.({ interval: undefined });
      return;
    }

    const value = parseInt(rawValue, 10);
    if (!isNaN(value) && value > 0) {
      onChange?.({ interval: value });
    }
  };

  // Tracks selected days
  const handleDayToggle = (day: TDayOfWeek, pressed: boolean) => {
    // Clear preset when user manually edits
    if (selectedPreset) {
      onChange?.({ selectedPreset: undefined });
    }

    if (pressed) {
      // Add day and sort by week order
      onChange?.({ selectedDays: sortDaysByWeekOrder([...selectedDays, day]) });
    } else {
      onChange?.({ selectedDays: selectedDays.filter(d => d !== day) });
    }
  };

  // Tracks start date
  const handleDateChange = (newDate: Date | undefined) => {
    // Clear preset when user manually edits
    if (selectedPreset) {
      onChange?.({ selectedPreset: undefined });
      // The preset derived nthWeekday from the previous date; drop it so monthly runs use the new date's day of month.
      // Left alone otherwise, since it may be a loaded schedule's ordinal (e.g. "2nd Monday") that the UI can't restore.
      onChange?.({ nthWeekday: undefined });
    }

    onChange?.({ startDate: newDate });
  };

  // Tracks start time
  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;

    // Always reflect the raw value so the user can type/clear the field freely.
    setStartTime(newTime);

    // Propagate empty so Save can disable (QA-618). Still skip partial mid-edit
    // values so the preview stays on the last valid time rather than flickering
    // or crashing while the user types. (ENG-3754)
    if (newTime !== '' && !isValidTimeStrict(newTime)) {
      return;
    }

    // Clear preset when user manually edits
    if (selectedPreset) {
      onChange?.({ selectedPreset: undefined });
    }

    onChange?.({ repeatStartTime: newTime });
  };

  // TODO: Re-enable when backend supports active_window
  // Tracks end time
  // const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   // Clear preset when user manually edits
  //   if (selectedPreset) {
  //     onChange?.({ selectedPreset: undefined });
  //   }
  //
  //   setEndTime(e.target.value);
  //   onChange?.({ repeatEndTime: e.target.value });
  // };

  // Tracks timezone
  const handleTimezoneChange = (value: string) => {
    setInternalTimezone(value);
    onChange?.({ timezone: value });
  };

  // Tracks frequency selection
  const handleFrequencyChange = (value: string) => {
    // Clear preset when user manually edits
    if (selectedPreset) {
      onChange?.({ selectedPreset: undefined });
    }

    setSelectedFrequency(value);
    onChange?.({ frequency: value as TRepeatFrequency });
  };

  // Handles preset selection and autofills form fields
  const handlePresetSelect = (preset: TSchedulePreset | undefined) => {
    onChange?.({ selectedPreset: preset });

    // If preset is unselected, reset to default values
    if (!preset) {
      onChange?.({ interval: 1 });
      setSelectedFrequency('Week');
      onChange?.({ frequency: 'Week' });
      onChange?.({ selectedDays: [] });
      onChange?.({ repeatStartTime: DEFAULT_START_TIME });
      onChange?.({ repeatEndTime: DEFAULT_END_TIME });
      onChange?.({ timezone: internalTimezone });
      return;
    }

    // Get the config from the preset
    const config = PRESET_AUTOFILL[preset];
    if (!config) {
      return;
    }

    // Autofill interval
    if (config.interval !== undefined) {
      onChange?.({ interval: config.interval });
    }

    // Autofill frequency
    if (config.frequency !== undefined) {
      setSelectedFrequency(config.frequency);
      onChange?.({ frequency: config.frequency });
    }

    // Autofill days - for weekly/monthly presets, use current day
    if (config.days !== undefined) {
      onChange?.({ selectedDays: config.days });
      onChange?.({ nthWeekday: undefined });
    } else if (
      preset === 'weekly-on-day' ||
      preset === 'monthly-on-nth-day' ||
      preset === 'monthly-on-last-day'
    ) {
      // Set to current day of week
      const currentDay = getDayOfWeekFromDate(referenceDate);
      onChange?.({ selectedDays: [currentDay] });

      // For monthly presets, also set the nth weekday occurrence
      if (preset === 'monthly-on-nth-day') {
        // Calculate which week of the month (1-5)
        const nthWeekday = Math.ceil(referenceDate.getDate() / 7);
        onChange?.({ nthWeekday });
      } else if (preset === 'monthly-on-last-day') {
        onChange?.({ nthWeekday: -1 }); // -1 indicates "last"
      } else {
        onChange?.({ nthWeekday: undefined });
      }
    }

    // Autofill start time (always include current start time)
    onChange?.({ repeatStartTime: DEFAULT_START_TIME });
    setStartTime(DEFAULT_START_TIME);

    // TODO: Re-enable when backend supports active_window
    // Autofill end time (clear if undefined in config)
    // if ('endTime' in config) {
    //   setEndTime(config.endTime || DEFAULT_END_TIME);
    //   onChange?.({ repeatEndTime: config.endTime });
    // } else {
    //   setEndTime(DEFAULT_END_TIME);
    //   onChange?.({ repeatEndTime: DEFAULT_END_TIME });
    // }

    // Include current timezone
    onChange?.({ timezone: internalTimezone });
  };

  // Hide days section for Day, Month, and Year frequencies
  const shouldHideDaysSection =
    selectedFrequency === 'Day' ||
    selectedFrequency === 'Month' ||
    selectedFrequency === 'Year';

  // TODO: Re-enable when backend supports active_window enforcement
  // Only show end time for Minute and Hour frequencies
  // const shouldShowEndTime =
  //   selectedFrequency === 'Minute' || selectedFrequency === 'Hour';

  return (
    <Card className="w-full" data-testid="schedule-repeats-card">
      <CardHeader>
        <Flex align="center" gap={2}>
          {/* TODO: Show badge when start and end cards are shown */}
          {/* <Badge variant="secondary" className="size-5 rounded-full">
            2
          </Badge> */}
          <Text level="base" weight="medium">
            {t('automation.schedule.repeats.title')}
          </Text>
        </Flex>
        <CardDescription>
          {t('automation.schedule.repeats.description')}
        </CardDescription>
      </CardHeader>

      <CardFooter>
        {/* Note: Need to add gap-9 to the flex component in the DS, using custom class for now */}
        <Flex direction="column" width="full" className="gap-9">
          <Flex direction="column" gap={4} width="full">
            {/* Schedule content */}
            <Flex
              direction="column"
              gap={6}
              width="full"
              justify="center"
              align="center"
            >
              {/* Every Section */}
              <ScheduleInputDropdown
                label={t('automation.schedule.repeats.every')}
                inputType="number"
                inputPlaceholder={t(
                  'automation.schedule.repeats.every.placeholder',
                )}
                inputValue={interval}
                onInputChange={handleIntervalChange}
                inputClassName="[&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden [-moz-appearance:textfield]"
                inputAriaLabel={t('automation.schedule.repeats.every')}
                inputTestId="schedule-repeats-interval-input"
                selectValue={selectedFrequency}
                selectPlaceholder={t(
                  'automation.schedule.repeats.frequency.week',
                )}
                onSelectChange={handleFrequencyChange}
                selectItems={FREQUENCY_ITEMS}
                selectAriaLabel={t('automation.schedule.repeats.frequency')}
                selectTestId="schedule-repeats-frequency-select"
                disabled={readonly}
              />

              {/* On Days Section - hidden for Day, Month, and Year frequencies */}
              {!shouldHideDaysSection && (
                <Flex
                  direction="column"
                  gap={3}
                  width="full"
                  data-testid="schedule-repeats-on-days-section"
                >
                  <Text level="small" weight="medium" className="px-1">
                    {t('automation.schedule.repeats.onDays')}
                  </Text>
                  <Flex
                    gap={1}
                    className="w-full"
                    role="group"
                    aria-label={t('automation.schedule.repeats.onDays')}
                  >
                    {DAYS_OF_WEEK.map(day => (
                      <Toggle
                        key={day.value}
                        pressed={selectedDays.includes(day.value)}
                        onPressedChange={pressed =>
                          handleDayToggle(day.value, pressed)
                        }
                        aria-label={day.value}
                        variant="outline"
                        size="default"
                        className="flex-1 min-w-0 bg-background cursor-pointer text-muted-foreground"
                        data-testid={`schedule-repeats-day-${day.value.toLowerCase()}`}
                        disabled={readonly}
                      >
                        {day.label}
                      </Toggle>
                    ))}
                  </Flex>
                </Flex>
              )}

              {/* During Section */}
              <Flex gap={2} align="end" width="full">
                <Flex direction="column" gap={3} className="shrink-0">
                  <Text level="small" weight="medium" className="px-1">
                    {t('automation.schedule.repeats.begin')}
                  </Text>
                  <Flex gap={2}>
                    <DatePicker
                      date={date}
                      onDateChange={handleDateChange}
                      placeholder={t(
                        'automation.schedule.repeats.beginDatePlaceholder',
                      )}
                      showIcon={false}
                      showChevron
                      format={DATE_FORMAT_TOKENS.DATE_INPUT}
                      buttonClassName="w-auto shrink-0"
                      aria-label={t('automation.schedule.repeats.beginDate')}
                      data-testid="schedule-repeats-begin-date-picker"
                      disabled={readonly}
                    />
                    <Input
                      type="time"
                      value={startTime}
                      onChange={handleStartTimeChange}
                      className="h-9 w-auto shrink-0 placeholder:text-muted-foreground text-foreground [&::-webkit-calendar-picker-indicator]:hidden"
                      aria-label={t('automation.schedule.repeats.begin')}
                      data-testid="schedule-repeats-begin-time-input"
                      disabled={readonly}
                    />
                  </Flex>
                </Flex>
                {/* TODO: Re-enable end time input when backend supports active_window enforcement
                     Backend edge case: active_window fields exist but are not checked at runtime
                     See: Schedule Edge Cases Analysis plan for details */}
                {/* {shouldShowEndTime && (
                  <ScheduleInputDropdown
                    label={t('automation.schedule.repeats.end')}
                    inputType="time"
                    inputValue={endTime}
                    inputClassName="[&::-webkit-calendar-picker-indicator]:hidden"
                    onInputChange={handleEndTimeChange}
                    inputAriaLabel={t('automation.schedule.repeats.end')}
                    inputTestId="schedule-repeats-end-time-input"
                  />
                )} */}

                {/* Timezone Selection */}
                <Flex direction="column" gap={3} flex="1" className="min-w-0">
                  <Text level="small" weight="medium" className="px-1">
                    {t('automation.schedule.repeats.timezone')}
                  </Text>
                  <Select
                    value={internalTimezone}
                    onValueChange={handleTimezoneChange}
                    disabled={readonly}
                  >
                    <Tooltip delayDuration={200}>
                      <TooltipTrigger asChild>
                        <SelectTrigger
                          className="h-9 w-full min-w-0"
                          aria-label={t('automation.schedule.repeats.timezone')}
                          data-testid="schedule-repeats-timezone-select"
                        >
                          <SelectValue />
                        </SelectTrigger>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        className="max-w-[300px] break-words"
                      >
                        {getTimezoneLabel(internalTimezone)}
                      </TooltipContent>
                    </Tooltip>
                    <SelectContent>
                      {getTimezoneGroupsWithFallback(
                        internalTimezone,
                        t('automation.schedule.repeats.timezoneOther'),
                      ).map(group => (
                        <SelectGroup key={group.label}>
                          <SelectLabel>{group.label}</SelectLabel>
                          {group.items.map(item => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </Flex>
              </Flex>
            </Flex>

            {/* Business Days Only Checkbox */}
            {/* TODO: Uncomment when BE supports */}
            {/* <Flex align="center" gap={2}>
              <Checkbox
                id="business-days-only"
                checked={businessDaysOnly}
                onCheckedChange={checked =>
                  onBusinessDaysOnlyChange?.(checked === true)
                }
                aria-label={t('automation.schedule.repeats.businessDaysOnly')}
                data-testid="business-days-checkbox"
              />
              <Label
                htmlFor="business-days-only"
                className="cursor-pointer text-muted-foreground"
              >
                {t('automation.schedule.repeats.businessDaysOnly')}
              </Label>
            </Flex> */}
          </Flex>

          {/* Separator with text */}
          <Flex direction="column" gap={4} width="full">
            <Flex align="center" gap={1} width="full">
              <Separator className="flex-1" />
              <Text
                level="small"
                weight="medium"
                color="muted"
                className="px-1 whitespace-nowrap"
              >
                {t('automation.schedule.repeats.separator')}
              </Text>
              <Separator className="flex-1" />
            </Flex>

            {/* Preset Options */}
            <SchedulePresets
              selectedPreset={selectedPreset}
              onPresetSelect={handlePresetSelect}
              referenceDate={referenceDate}
              disabled={readonly}
            />
          </Flex>
        </Flex>
      </CardFooter>
    </Card>
  );
}
